'use client';

import { useQuery } from '@tanstack/react-query';
import {
  UsersApi,
  WorkspaceMembersApi,
  WorkspaceMemberStatus,
  type WorkspaceMemberLookupRowDto,
} from '@up2stay/up2promo-api-client';
import {
  AssigneeRosterPanel,
  type AssigneeRosterPanelLabels,
  type AssigneeRosterPerson,
  Avatar,
  AvatarFallback,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from '@up2stay/up2promo-ui';
import { Check, ChevronDown, Lock, UserRound } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import {
  formatAssigneeLockTooltip,
  listAssigneeEditors,
} from '@/core/components/leads/assignee-lock-tooltip';
import { personInitials } from '@/core/components/leads/leads-list-display';
import { useWorkspaceRoles } from '@/core/hooks/use-workspace-roles';
import { useWorkspaceMembersRoster } from '@/core/hooks/use-workspace-members-roster';
import { triggerSelectionHaptic } from '@/core/lib/dnd-haptics';
import { getAbsoluteFileUrl } from '@/core/lib/file-url';
import { workspaceMembersQueryKeys } from '@/core/query-keys/workspace-members-query-keys';
import { useAuthStore } from '@/core/store/auth.store';

function useDebouncedValue<T>(value: T, ms: number): T {
  const [d, setD] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setD(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return d;
}

function optionalString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function lookupMemberLabel(row: WorkspaceMemberLookupRowDto): string {
  const u = row.user;
  if (!u) return row.userId?.trim() ?? '';
  const name =
    `${optionalString(u.firstName)} ${optionalString(u.lastName)}`.trim();
  return name || optionalString(u.username) || row.userId.trim();
}

function lookupMemberPhotoUrl(
  row: WorkspaceMemberLookupRowDto,
): string | undefined {
  const path = row.user?.photo?.path;
  if (typeof path !== 'string' || !path.trim()) return undefined;
  const url = getAbsoluteFileUrl(path);
  return url || undefined;
}

function isActiveMember(row: WorkspaceMemberLookupRowDto): boolean {
  const s = row.status;
  if (s === WorkspaceMemberStatus.ACTIVE) return true;
  if (String(s ?? '').toLowerCase() === 'active') return true;
  if (s === undefined || s === null) return !!row.userId?.trim();
  return false;
}

export type LeadAssigneePickerLabels = AssigneeRosterPanelLabels & {
  savedCue: string;
  lockedTooltip: string;
  /** `{names}` — named editors when the list fits the cap. */
  lockedTooltipNamed: string;
  /** `{names}` + `{count}` — overflow form. */
  lockedTooltipNamedMore: string;
  assignedToast: string;
  clearedToast: string;
};

export type LeadAssigneeRosterPerson = AssigneeRosterPerson;

export type LeadAssigneePickerProps = {
  workspaceId: string;
  ownerUserId: string;
  ownerOptionLabel: string;
  /** Explicit assignee; null/undefined/'' = unassigned (Niemand). */
  assigneeUserId: string | null | undefined;
  triggerLabel: string;
  triggerClassName?: string;
  disabled?: boolean;
  editable?: boolean;
  placeholder: string;
  labels: LeadAssigneePickerLabels;
  locale: string;
  isPrivate?: boolean;
  workspaceLeadsAlwaysShared?: boolean;
  presentation?: 'popover' | 'inline';
  confirmLayout?: 'popover' | 'sheet';
  hideNobody?: boolean;
  selectedCount?: number;
  privateShareCount?: number;
  assignmentCountsByUserId?: ReadonlyMap<string, number>;
  showSavedCue?: boolean;
  /** null = unassigned; string = that user (including owner when explicitly chosen). */
  onAssigneeChange: (nextUserId: string | null, selectedLabel: string) => void;
};

/**
 * Domain-wired assignee picker. Presentational list lives in `@up2stay/up2promo-ui`.
 * Null assignee = Niemand (unassigned); owner is a normal selectable member.
 */
export function LeadAssigneePicker({
  workspaceId,
  ownerUserId,
  ownerOptionLabel,
  assigneeUserId,
  triggerLabel,
  triggerClassName,
  disabled = false,
  editable = true,
  placeholder,
  labels,
  locale,
  isPrivate = false,
  workspaceLeadsAlwaysShared = false,
  presentation = 'popover',
  confirmLayout = presentation === 'inline' ? 'sheet' : 'popover',
  hideNobody = false,
  selectedCount = 0,
  privateShareCount = 0,
  assignmentCountsByUserId,
  showSavedCue = false,
  onAssigneeChange,
}: LeadAssigneePickerProps) {
  const currentUserId = useAuthStore((s) => s.user?.id?.trim() ?? '');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [pendingShare, setPendingShare] = useState<AssigneeRosterPerson | null>(
    null,
  );
  const [justSaved, setJustSaved] = useState(false);
  const debouncedSearch = useDebouncedValue(search, 300);

  const ownerTrim = ownerUserId.trim();
  const selectedUserId = assigneeUserId?.trim() ? assigneeUserId.trim() : null;
  const isOthersPrivate =
    !!isPrivate && !!ownerTrim && ownerTrim !== currentUserId;

  const queryEnabled = !!workspaceId && (presentation === 'inline' || open);
  const lockRosterEnabled = !!workspaceId && !editable;

  const { data: lockRoster = [] } = useWorkspaceMembersRoster(
    workspaceId,
    'assignee-lock',
    { enabled: lockRosterEnabled, staleTime: 60_000 },
  );
  const { data: lockRoles = [] } = useWorkspaceRoles(
    lockRosterEnabled ? workspaceId : undefined,
  );

  const resolvedLockedTooltip = useMemo(() => {
    if (editable) return labels.lockedTooltip;
    const names = listAssigneeEditors({
      members: lockRoster,
      roles: lockRoles,
      ownerUserId: ownerTrim,
      isPrivate: !!isPrivate,
      isOthersPrivate,
    });
    return formatAssigneeLockTooltip(names, {
      lockedTooltip: labels.lockedTooltip,
      lockedTooltipNamed: labels.lockedTooltipNamed,
      lockedTooltipNamedMore: labels.lockedTooltipNamedMore,
    });
  }, [
    editable,
    isOthersPrivate,
    isPrivate,
    labels.lockedTooltip,
    labels.lockedTooltipNamed,
    labels.lockedTooltipNamedMore,
    lockRoles,
    lockRoster,
    ownerTrim,
  ]);

  const {
    data: rows = [],
    isFetching,
    isError,
  } = useQuery({
    queryKey: workspaceMembersQueryKeys.list(workspaceId, debouncedSearch),
    queryFn: async () => {
      const res = await WorkspaceMembersApi.lookup({
        path: { workspaceId },
        query: {
          page: 1,
          limit: 80,
          ...(debouncedSearch.trim() ? { search: debouncedSearch.trim() } : {}),
        },
      });
      return res.data?.data ?? [];
    },
    enabled: queryEnabled,
    staleTime: 30 * 1000,
    retry: 1,
  });

  const formerUserId =
    selectedUserId &&
    !rows.some((r) => isActiveMember(r) && r.userId?.trim() === selectedUserId)
      ? selectedUserId
      : null;

  const { data: formerUser } = useQuery({
    queryKey: workspaceMembersQueryKeys.resolve(
      workspaceId,
      formerUserId ?? '',
    ),
    queryFn: async () => {
      if (!formerUserId) return null;
      try {
        const res = await UsersApi.findOne({ path: { id: formerUserId } });
        return res.data ?? null;
      } catch {
        return null;
      }
    },
    enabled: queryEnabled && !!formerUserId,
    staleTime: 60 * 1000,
    retry: false,
  });

  const people = useMemo<AssigneeRosterPerson[]>(() => {
    const active = rows.filter(isActiveMember);
    const map = new Map<string, AssigneeRosterPerson>();
    for (const row of active) {
      const uid = row.userId?.trim();
      if (!uid) continue;
      const label = lookupMemberLabel(row) || uid;
      map.set(uid, {
        userId: uid,
        label,
        initials: personInitials(label),
        photoUrl: lookupMemberPhotoUrl(row),
        isCurrentUser: !!currentUserId && uid === currentUserId,
        assignedCount: assignmentCountsByUserId?.get(uid),
      });
    }

    if (ownerTrim && !map.has(ownerTrim)) {
      const ownerLabel = ownerOptionLabel.trim() || ownerTrim;
      map.set(ownerTrim, {
        userId: ownerTrim,
        label: ownerLabel,
        initials: personInitials(ownerLabel),
        isCurrentUser: !!currentUserId && ownerTrim === currentUserId,
        assignedCount: assignmentCountsByUserId?.get(ownerTrim),
      });
    }

    const list = [...map.values()].sort((a, b) =>
      a.label.localeCompare(b.label, locale === 'de' ? 'de' : 'en', {
        sensitivity: 'base',
      }),
    );

    if (formerUserId && !map.has(formerUserId)) {
      const resolvedName = formerUser
        ? `${formerUser.firstName ?? ''} ${formerUser.lastName ?? ''}`.trim() ||
          formerUser.email ||
          formerUser.username ||
          ''
        : '';
      const label = resolvedName || triggerLabel.trim() || formerUserId;
      list.unshift({
        userId: formerUserId,
        label,
        initials: personInitials(label),
        isCurrentUser: formerUserId === currentUserId,
        isFormerMember: true,
        assignedCount: assignmentCountsByUserId?.get(formerUserId),
      });
    }

    return list;
  }, [
    rows,
    locale,
    currentUserId,
    formerUserId,
    formerUser,
    triggerLabel,
    assignmentCountsByUserId,
    ownerTrim,
    ownerOptionLabel,
  ]);

  const displayLabel = triggerLabel.trim()
    ? triggerLabel
    : selectedUserId
      ? placeholder
      : labels.nobody;

  const commit = (nextUserId: string | null, selectedLabel: string) => {
    onAssigneeChange(nextUserId, selectedLabel);
    setPendingShare(null);
    setSearch('');
    setOpen(false);
    if (showSavedCue) {
      setJustSaved(true);
      window.setTimeout(() => setJustSaved(false), 1600);
    }
  };

  const needsShareConfirm = (nextUserId: string | null) => {
    if (workspaceLeadsAlwaysShared || !isPrivate) return false;
    const next = nextUserId?.trim() ?? '';
    if (!next) return false;
    // Share only when assigning to someone other than the record owner.
    return next !== ownerTrim;
  };

  const requestSelectPerson = (person: AssigneeRosterPerson) => {
    if (person.isFormerMember) return;
    if (needsShareConfirm(person.userId)) {
      setPendingShare(person);
      return;
    }
    void triggerSelectionHaptic();
    commit(person.userId, person.label);
  };

  const requestSelectNobody = () => {
    void triggerSelectionHaptic();
    commit(null, labels.nobody);
  };

  const panel = (
    <AssigneeRosterPanel
      labels={labels}
      people={people}
      selectedUserId={selectedUserId}
      search={search}
      onSearchChange={setSearch}
      isLoading={isFetching}
      isError={isError}
      pendingShare={pendingShare}
      onCancelShare={() => setPendingShare(null)}
      onConfirmShare={() => {
        if (!pendingShare) return;
        void triggerSelectionHaptic();
        commit(pendingShare.userId, pendingShare.label);
      }}
      onSelectPerson={requestSelectPerson}
      onSelectNobody={requestSelectNobody}
      hideNobody={hideNobody}
      selectedCount={selectedCount}
      privateShareCount={privateShareCount}
      confirmLayout={confirmLayout}
    />
  );

  if (presentation === 'inline') {
    if (!editable) {
      return (
        <p className="px-3 py-4 text-[13px] text-muted-foreground text-pretty">
          {resolvedLockedTooltip}
        </p>
      );
    }
    return panel;
  }

  const triggerBody = (
    <span className="inline-flex min-w-0 max-w-full items-center gap-1.5">
      {selectedUserId ? (
        <Avatar className="size-[22px] shrink-0">
          <AvatarFallback className="bg-[color:var(--ghost-accent-bg)] text-[9px] font-bold text-[color:var(--ghost-accent-fg)]">
            {personInitials(displayLabel)}
          </AvatarFallback>
        </Avatar>
      ) : (
        <span className="inline-flex size-[22px] shrink-0 items-center justify-center rounded-full border border-dashed border-muted-foreground/40 text-muted-foreground">
          <UserRound className="size-3" aria-hidden />
        </span>
      )}
      <span className="min-w-0 truncate text-left">
        {displayLabel || placeholder}
      </span>
      {justSaved ? (
        <span className="inline-flex shrink-0 items-center gap-0.5 text-[11px] font-medium text-emerald-600">
          <Check className="size-3" aria-hidden />
          {labels.savedCue}
        </span>
      ) : null}
      {editable ? (
        <ChevronDown className="size-3.5 shrink-0 opacity-50" aria-hidden />
      ) : (
        <Lock className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
      )}
    </span>
  );

  if (!editable) {
    return (
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className={cn(triggerClassName, 'cursor-default opacity-90')}
              disabled={disabled}
            >
              {triggerBody}
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-xs text-pretty">
            {resolvedLockedTooltip}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          setSearch('');
          setPendingShare(null);
        }
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            triggerClassName,
            disabled && 'cursor-not-allowed opacity-60',
          )}
          disabled={disabled || !workspaceId}
        >
          {triggerBody}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[min(100vw-2rem,320px)] p-0"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          const input = (e.currentTarget as HTMLElement).querySelector('input');
          input?.focus();
        }}
      >
        {panel}
      </PopoverContent>
    </Popover>
  );
}

/** @deprecated Prefer LeadAssigneePicker */
export { LeadAssigneePicker as LeadAssigneeCombobox };

export type { AssigneeRosterPanelProps } from '@up2stay/up2promo-ui';
export { AssigneeRosterPanel as LeadAssigneeRosterPanel } from '@up2stay/up2promo-ui';
